import { Group, Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { ComponentType, JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { EGoalType, Goal } from '@feathr/blackbox';
import { Button, ButtonValid, Drawer, InlineEdit, Pill, Well } from '@feathr/components';
import ConversionPixelTriggerGoal from '@feathr/extender/components/Goal/ConversionPixelTriggerGoal';
import DonationFormSubmissionGoal from '@feathr/extender/components/Goal/DonationFormSubmissionGoal';
import FormSubmissionGoal from '@feathr/extender/components/Goal/FormSubmissionGoal';
import GoalTypeSelect from '@feathr/extender/components/Goal/GoalLayout/GoalTypeSelect';
import GroupEntryGoal from '@feathr/extender/components/Goal/GroupEntryGoal';
import URLVisitGoal from '@feathr/extender/components/Goal/URLVisitGoal';
import { cssVar } from '@feathr/hooks';

import { useGoalDrawerForm } from './GoalDrawer.hooks';
import GoalDrawerEditWarning from './GoalDrawerEditWarning';

import * as styles from './GoalDrawer.css';

interface IGoalDrawerBaseProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateGoal?: () => void;
}

interface IGoalDrawerCreateProps extends IGoalDrawerBaseProps {
  goal?: never;
  mode?: 'create';
}

interface IGoalDrawerEditProps extends IGoalDrawerBaseProps {
  goal: Goal;
  mode: 'edit';
}

export type IGoalDrawerProps = IGoalDrawerCreateProps | IGoalDrawerEditProps;

const typeComponentMap: Record<EGoalType, ComponentType<{ goal: Goal }>> = {
  conversion_pixel: ConversionPixelTriggerGoal,
  donation: DonationFormSubmissionGoal,
  form_submission: FormSubmissionGoal,
  group_entry: GroupEntryGoal,
  page_view: URLVisitGoal,
};

function GoalDrawer({
  goal: editGoal,
  isOpen,
  mode = 'create',
  onClose,
  onCreateGoal,
}: Readonly<IGoalDrawerProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const {
    acknowledged,
    activeCampaignsCount,
    buttonErrors,
    goal,
    handleSave,
    isSaving,
    isValid,
    nameErrors,
    reset,
    setAcknowledged,
    totalConversions,
  } = useGoalDrawerForm(onClose, isOpen, mode, editGoal);

  function handleClose(): void {
    reset();
    onClose();
  }

  const convType = goal?.get('conv_type');
  const TypeSpecificForm =
    convType !== undefined && convType in typeComponentMap
      ? typeComponentMap[convType as EGoalType]
      : null;

  const isEditMode = mode === 'edit';
  const drawerTitle = isEditMode ? t('Edit goal') : t('Create goal');
  const submitLabel = isEditMode ? t('Save changes') : t('Create goal');
  const submitName = isEditMode ? 'save-goal' : 'create-goal';

  return (
    <Drawer
      description={
        isEditMode
          ? t("Changes apply to the account-level goal everywhere it's tracked.")
          : t("Define a goal once — it'll track across every campaign and flight.")
      }
      isOpen={isOpen}
      name={isEditMode ? 'edit-goal-drawer' : 'create-goal-drawer'}
      onClose={handleClose}
      title={drawerTitle}
      width={cssVar('--drawer-width-144')}
    >
      <Drawer.Content addVerticalGap={true}>
        {isEditMode && (
          <GoalDrawerEditWarning
            acknowledged={acknowledged}
            activeCampaignsCount={activeCampaignsCount}
            onAcknowledge={setAcknowledged}
            onCreateGoal={onCreateGoal}
            totalConversions={totalConversions}
          />
        )}
        {goal && (
          <Well borderless={true}>
            <Stack gap={theme.spacing['4']} w={'100%'}>
              <Group>
                {/* TODO: Render conditionally when goal is primary on ≥1 campaign and once the BE adds the stats  */}
                {isEditMode && (
                  <Pill theme={'cyan'} variant={'secondary'}>
                    {t('Primary')}
                  </Pill>
                )}
                <InlineEdit
                  attribute={'name'}
                  className={styles.title}
                  model={goal}
                  validationError={nameErrors[0]}
                />
              </Group>
              <GoalTypeSelect goal={goal} isAdvancedConversionTracking={false} />
              {TypeSpecificForm && <TypeSpecificForm goal={goal} />}
            </Stack>
          </Well>
        )}
      </Drawer.Content>
      <Drawer.Actions>
        <Button name={'cancel'} onClick={handleClose}>
          {t('Cancel')}
        </Button>
        <ButtonValid
          disabled={!isValid}
          errors={buttonErrors}
          isLoading={isSaving}
          name={submitName}
          onClick={handleSave}
          type={'primary'}
        >
          {submitLabel}
        </ButtonValid>
      </Drawer.Actions>
    </Drawer>
  );
}

export default observer(GoalDrawer);
